/* eslint-disable no-use-before-define */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
	Layer,
	Box,
	Card,
	CardBody,
	CardFooter,
	Button,
	Text,
	CardHeader,
	Image,
	CheckBox,
} from 'grommet';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import turnLoudnessDetector from '@/helpers/loudnessDetector';
import { usePublisherContext } from '@/contexts/usePublisher';
import { useGetBackgroundImagesLazyQuery } from '@/apollo/gql/generated/graphql';
import { useViewerContext } from '@/contexts/useViewer';
import { NotificationKeys } from '@/utils/notifications';
import { CloseIcon, HeadphonesIcon, CameraIcon, WarningIcon, Bell } from '../icons';
import DeviceSelector from '../call/DeviceSelector';
import VideoHolder from '../call/VideoHolder';

const SettingsModal = ({
	show,
	setShow,
	openTab,
}: {
	show: boolean;
	setShow: (arg: boolean) => void;
	openTab?: string;
}) => {
	// const { stream, getStreams } = useTempDevicesContext();

	const router = useRouter();
	const { id } = router.query;
	const {
		clearBackground,
		blurBackground,
		setBackground,
		tempVideoElement,
		tempPublisher,
		applyFilters,
		createTempPublisher,
		destroyTempPublisher,
		loadingFilter,
		canApplyFilters,
		applyMic,
	} = usePublisherContext();
	const {isStudent} = useViewerContext();
	const [activeTab, setActiveTab] = useState(openTab || 'video');
	const volumeRef = useRef();
	const { t } = useTranslation('default');

	const [getBackgroundImages, { data }] = useGetBackgroundImagesLazyQuery({
		variables: { filter: { classSlug: id as string } },
	});

	useEffect(() => {
		if (!id || !canApplyFilters) return;
		getBackgroundImages({ variables: { filter: { classSlug: id as string } } });
	}, [getBackgroundImages, id, canApplyFilters]);

	useEffect(() => {
		if (activeTab === 'video') return;
		const micId = tempPublisher?.getAudioSource()?.id;
		if (!micId) return;
		turnLoudnessDetector.turnLoudnessDetectorOn({
			selectedMicrophoneId: micId,
			isAudioEnabled: true,
			indicator: volumeRef,
		});
		return () => {
			turnLoudnessDetector.turnLoudnessDetectorOff();
		};
	}, [tempPublisher, activeTab]);

	useEffect(() => {
		createTempPublisher();
		return () => {
			destroyTempPublisher();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const [notificationStates, setNotificationStates] = useState({
		hand_raised_audio: localStorage.getItem(NotificationKeys.HAND_RAISED_AUDIO_OFF) !== 'true',
		hand_raised_popup: localStorage.getItem(NotificationKeys.HAND_RAISED_POPUP_OFF) !== 'true',
		join_class_audio: localStorage.getItem(NotificationKeys.JOIN_CLASS_AUDIO_OFF) !== 'true',
		join_class_popup: localStorage.getItem(NotificationKeys.JOIN_CLASS_POPUP_OFF) !== 'true',
		messages_audio: localStorage.getItem(NotificationKeys.MESSAGES_AUDIO_OFF) !== 'true',
		messages_popup: localStorage.getItem(NotificationKeys.MESSAGES_POPUP_OFF) !== 'true',
	});

	const handleNotificationChange = (type: string, notificationType: 'audio' | 'popup') => {
		const key = `${type}_${notificationType}`;
		const storageKey = `${key}_off`;
		
		setNotificationStates(prev => {
			const newValue = !prev[key as keyof typeof prev];
			if (newValue) {
				localStorage.removeItem(storageKey);
			} else {
				localStorage.setItem(storageKey, 'true');
			}
			return {
				...prev,
				[key]: newValue
			};
		});
	};

	const notificationScreen = useMemo(() => {
		return <SettingsModalBodyInner>
			<Box>
				<Box gap="medium">
					<Box direction="row" align="center" justify="between" pad="small" background="light-1">
						<Text color="black" weight="bold">{t('settings_modal.notifications')}</Text>
						<Box direction="row" gap="medium">
							<Text color="black" size="small">{t('settings_modal.audio')}</Text>
							<Text color="black" size="small">{t('settings_modal.popup')}</Text>
						</Box>
					</Box>

					{!isStudent && <Box direction="row" align="center" justify="between" pad="small">
						<Text color="black">Raise Hand</Text>
						<Box direction="row" gap="large" align="center">
							<CheckBox
								checked={notificationStates.hand_raised_audio}
								onChange={() => handleNotificationChange('hand_raised', 'audio')}
							/>
							<CheckBox  
								checked={notificationStates.hand_raised_popup}
								onChange={() => handleNotificationChange('hand_raised', 'popup')}
							/>
						</Box>
					</Box>}

					{!isStudent && <Box direction="row" align="center" justify="between" pad="small">
						<Text color="black">Join Class</Text>
						<Box direction="row" gap="large" align="center">
							<CheckBox
								checked={notificationStates.join_class_audio}
								onChange={() => handleNotificationChange('join_class', 'audio')}
							/>
							<CheckBox
								checked={notificationStates.join_class_popup}
								onChange={() => handleNotificationChange('join_class', 'popup')}
							/>
						</Box>
					</Box>}

					<Box direction="row" align="center" justify="between" pad="small">
						<Text color="black">Messages</Text>
						<Box direction="row" gap="large" align="center">
							<CheckBox
								checked={notificationStates.messages_audio}
								onChange={() => handleNotificationChange('messages', 'audio')}
							/>
							<CheckBox
									checked={notificationStates.messages_popup}
									onChange={() => handleNotificationChange('messages', 'popup')}
								/>
						</Box>
					</Box>
				</Box>
			</Box>
		</SettingsModalBodyInner>;
	}, [notificationStates, t]);

	if (show) {
		return (
			<Layer
				onEsc={() => setShow(false)}
				onClickOutside={() => setShow(false)}
				background="transparent"
			>
				<SettingsModalWrap height="auto" width="large">
					<CardBody>
						<ButtonClose
							onClick={() => setShow(false)}
							icon={<CloseIcon />}
							hoverIndicator
						/>
						<Box direction="row">
							<SettingsModalSB>
								<SettingsModalHeader>
									<span>{t('settings_modal.call_settings')}</span>
								</SettingsModalHeader>
								<SettingsModalSBNav>
									<SBNavItem
										onClick={() => setActiveTab('video')}
										className={activeTab === 'video' ? 'active' : ''}
									>
										<CameraIcon className="icon" />
										{t('settings_modal.video')}
									</SBNavItem>
									<SBNavItem
										onClick={() => setActiveTab('audio')}
										className={activeTab === 'audio' ? 'active' : ''}
									>
										<HeadphonesIcon className="icon" />
										{t('settings_modal.audio')}
									</SBNavItem>
									<SBNavItem
										onClick={() => setActiveTab('notifications')}
										className={activeTab === 'notifications' ? 'active' : ''}
									>
										<Bell className="bell" width="20px" height="20px" />
										{t('settings_modal.notifications')}
									</SBNavItem>
								</SettingsModalSBNav>
							</SettingsModalSB>

							<SettingsModalBody>
								<SettingsModalHeader>
									<span>{t('settings_modal.av_options')}</span>
								</SettingsModalHeader>

								{activeTab === 'video' && (
									<SettingsModalBodyInner>
										<CameraWrap>
											<VideoHolder
												videoStatus
												videoElement={tempVideoElement}
											/>
										</CameraWrap>

										<FromGroup>
											<Text color="black"> {t('settings_modal.camera')}</Text>
											<DeviceSelectorStyled
												deviceType="video"
												saveLocal={false}
											/>
										</FromGroup>

										{canApplyFilters && (
											<GridWrap>
												<GridWrapInner>
													<Box
														width="150px"
														height="80px"
														margin="0 10px 0 0"
														background="gray"
														flex
														align="center"
														justify="center"
														round="xsmall"
														onClick={clearBackground}
													>
														<Text>None</Text>
													</Box>
													<Box
														width="150px"
														height="80px"
														margin="0 10px 0 0"
														background="lightGray"
														flex
														align="center"
														justify="center"
														round="xsmall"
														onClick={blurBackground}
													>
														<Text>Blur</Text>
													</Box>
													{data?.getBackgroundImages?.map((image) => (
														<Box key={`image-${image?.id}`} margin="0 10px 0 0">
															<Button
																disabled={loadingFilter}
																onClick={() =>
																	setBackground(
																		`/aws-bucket/${image?.key as string}`
																	)
																}
															>
																<Image
																	src={`/aws-bucket/${image?.key as string}`}
																	alt={image?.imgName}
																	width="auto"
																	height="80px"
																	width="150px"
																	round="xsmall"
																/>
															</Button>
														</Box>
													))}
												</GridWrapInner>
											</GridWrap>
										)}
										{!canApplyFilters && (
											<NoFilterSupport gap="small" pad="medium">
												<WarningIcon
													color="#f7ba16"
													width="40px"
													height="40px"
												/>
												<Text color="brand">
													{t('visual_effects_not_compatible')}
												</Text>
											</NoFilterSupport>
										)}
										<Box margin="medium">
											<SaveSettingsButton
												primary
												label={t('save_settings')}
												onClick={applyFilters}
											/>
										</Box>
									</SettingsModalBodyInner>
								)}
								{activeTab === 'audio' && (
									<SettingsModalBodyInner>
										<FromGroup>
											<Text color="black">{t('settings_modal.mic')}</Text>
											<DeviceSelectorStyled
												deviceType="audio"
												saveLocal={false}
											/>
										</FromGroup>

										<LevelControlGroup>
											<span>{t('settings_modal.output_level')}:</span>
											<LevelVisualizer>
												<Visualizer ref={volumeRef} />
											</LevelVisualizer>
										</LevelControlGroup>
										<Box margin="medium">
											<SaveSettingsButton
												primary
												label={t('save_settings')}
												onClick={applyMic}
											/>
										</Box>
									</SettingsModalBodyInner>
								)}
								{activeTab === 'notifications' && notificationScreen}
							</SettingsModalBody>
						</Box>
					</CardBody>
					<CardFooter pad={{ horizontal: 'small' }} background="light-2" />
				</SettingsModalWrap>
			</Layer>
		);
	}
	return null;
};

export default SettingsModal;

export const SettingsModalWrap = styled(Card)`
	position: relative;
	background: #fff;
	border-radius: 8px;
	margin: 40px 0;
`;
export const NoFilterSupport = styled(Box)`
	color: black;
	display: flex;
	align-items: center;
`;

export const ButtonClose = styled(Button)`
	position: absolute;
	top: 0;
	right: 0;
	height: 80px;
	padding: 0 20px;
`;

export const SaveSettingsButton = styled(Button)`
	color: white;
	&:hover {
		box-shadow: none;
	}
`;

export const SettingsModalHeader = styled.div`
	border-bottom: 1px solid #cccccc;
	color: #000000;
	padding: 0 20px;
	height: 80px;
	display: flex;
	flex-shrink: 0;
	align-items: center;
	align-content: center;
	width: 100%;
	box-sizing: border-box;

	span {
		color: #000000;
		font-weight: bold;
	}
`;

export const SettingsModalSB = styled(CardHeader)`
	min-width: 220px;
	flex-direction: column;
	justify-content: flex-start;
	align-items: flex-start;
	border-right: 1px solid #cccccc;
	background: #f5f5f5;
`;

export const SettingsModalSBNav = styled.div`
	padding: 10px;
	width: 100%;
	box-sizing: border-box;
`;

export const SBNavItem = styled.div`
	width: 100%;
	box-sizing: border-box;
	padding: 10px;
	border-radius: 4px;
	color: #000000;
	display: flex;
	align-items: center;
	align-content: center;

	&.active,
	&:hover {
		background: rgba(0, 0, 0, 0.05);
	}

	.icon {
		width: 20px;
		height: 20px;
		margin-right: 6px;
		color: #000;
	}
`;

export const SettingsModalBody = styled(Box)`
	flex: 1;
`;

export const SettingsModalBodyInner = styled.div`
	padding: 20px 20px 25px;
	flex: 1;
	height: 100%;
	max-height: 100%;
	overflow: scroll;
`;

export const FromGroup = styled(Box)`
	margin-bottom: 10px;

	span {
		font-weight: bold;
		font-size: 14px;
	}

	button {
		width: 100%;
		border: 1px solid rgba(0, 0, 0, 0.33);
		color: #000;
	}
`;

export const DeviceSelectorStyled = styled(DeviceSelector)`
	color: 'black';
`;

export const FromFooter = styled(Box)`
	margin-top: 20px;
	button {
		width: 100%;
	}
`;

export const LevelControlGroup = styled(Box)`
	display: flex;
	flex-direction: row;
	align-items: center;

	span {
		font-size: 14px;
		font-weight: bold;
		color: #000;
		margin-right: 10px;
	}

	svg {
		height: 20px;
	}
`;

export const LevelVisualizer = styled.div`
	flex: 1;
	height: 8px;
	background: #f5f5f5;
	border: 1px solid #cccccc;
	border-radius: 5px;
	margin-right: 5px;
`;

export const Visualizer = styled(Box)`
	display: block;
	box-sizing: border-box;
	width: ${(props) => props.width};
	height: 100%;
	border-radius: 5px;
	background: rgb(136, 218, 145);
	background: linear-gradient(
		90deg,
		rgba(136, 218, 145, 1) 0%,
		rgba(250, 255, 0, 1) 50%,
		rgba(239, 62, 51, 1) 100%
	);
`;

export const CameraWrap = styled.div`
	aspect-ratio: 16 / 9;
	width: 100%;
	border-radius: 4px;
	background: #f5f5f5;
	margin-bottom: 10px;
	overflow: hidden;
	video {
		object-fit: cover;
		height: 290px;
	}
`;

export const GridWrap = styled.div`
	position: relative;
	height: 110px;
	width: 100%;
	overflow: hidden;
	overflow-x: auto;
`;

export const GridWrapInner = styled.div`
	position: absolute;
	left: 0;
	top: 5px;
	display: flex;
	justify-content: flex-start;
	flex-wrap: no-wrap;
	width: auto;

	img {
		border-radius: 6px;
	}
`;
